<script lang="ts" setup>
import type { SearchResult } from "~/composables/useSiteSearch";

defineProps({
  iconSize: { type: String, required: false, default: "1.5rem" },
});

const { query, results, resolve } = useSiteSearch();
const { t, te } = useI18n();
const route = useRoute();

// Shown before anything is typed, so the dialog says what it can do rather
// than sitting empty. Four curated queries in a fixed order — booking, menu,
// ski, cameras — translated per locale.
//
// These used to be four drawn at random from a pool of fourteen, on the theory
// that a returning visitor should see something new. The theory was wrong: the
// chips are a statement of what the site is for, and a visitor who opens search
// twice should meet the same answer rather than a different quarter of the pool
// each time. Curating them also means every chip earns its slot, instead of
// `bus` or `dogs` turning up in one of four.
//
// Still discovered by counting up from searchExample1 until a key is missing,
// so the set stays a locale-file change. tests/searchIndex.test.ts asserts each
// one returns results in every language — a suggestion that finds nothing is
// worse than no suggestion.
const exampleKeys = computed(() => {
  const keys: string[] = [];
  for (let n = 1; te(`searchExample${n}`); n++) keys.push(`searchExample${n}`);
  return keys;
});

const examples = computed(() => exampleKeys.value.map(key => t(key)));

function applyExample(example: string) {
  query.value = example;
  inputRef.value?.focus();
}

const open = ref(false);
const activeIndex = ref(0);

const triggerRef = ref<HTMLButtonElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);

const activeId = computed(() => (results.value.length ? `site-search-result-${activeIndex.value}` : undefined));

// Shared with MobileNav, which this dialog can open on top of — see
// composables/useScrollLock.ts for why it lives on <html> and is ref-counted.
const scrollLock = useScrollLock();

function show() {
  open.value = true;
  scrollLock.lock();
}

function hide() {
  // The route watcher below calls this on every navigation, dialog open or not.
  if (!open.value) return;
  open.value = false;
  // Released synchronously, before navigateTo() runs, so the router's
  // scrollBehavior never sees a locked viewport.
  scrollLock.unlock();
}

watch(open, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    inputRef.value?.focus();
  } else {
    query.value = "";
    // preventScroll: the trigger sits in a fixed nav, so there is nothing to
    // scroll to — and doing so would fight a hash landing already in flight.
    triggerRef.value?.focus({ preventScroll: true });
  }
});

// A shorter result list can leave the cursor pointing past the end. Watching
// the query rather than `results` matters: subscribing to `results` at setup
// would evaluate the whole resolved index — every t() for every entry — on
// hydration of every page and again on each locale switch, with the dialog
// closed and the answer always [].
watch(query, () => {
  activeIndex.value = 0;
});

function move(delta: number) {
  if (results.value.length === 0) return;
  const next = activeIndex.value + delta;
  // Wrap, so holding the down arrow cycles instead of sticking at the bottom.
  activeIndex.value = (next + results.value.length) % results.value.length;
}

// Nothing behind the overlay should be reachable while it is open, and the
// panel is teleported to <body> so tab order would otherwise run straight
// past it into the page. Same approach as MobileNav.vue.
function focusables(): HTMLElement[] {
  return panelRef.value
      ? Array.from(panelRef.value.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled])'))
      : [];
}

function onTab(event: KeyboardEvent) {
  const items = focusables();
  if (items.length === 0) return;

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

async function go(result: SearchResult | undefined) {
  if (!result) return;
  hide();
  await navigateTo(resolve(result.entry));
  if (result.entry.hash) await focusSection(result.entry.hash);
}

// Lands focus on the section for keyboard and screen-reader users. vue-router
// only calls scrollTo, and the close watcher above has just put focus back on
// the trigger in the nav — the wrong place to continue reading from.
//
// navigateTo() resolves before the router's scrollBehavior has run (vue-router
// does not await it), so on a cross-page navigation into a page whose setup
// suspends the section is not mounted yet. Wait for it, briefly. preventScroll:
// the router positions the section under the nav offset, and a focus scroll
// would undo that.
async function focusSection(id: string) {
  for (let frame = 0; frame < 60; frame++) {
    const section = document.getElementById(id);
    if (section) {
      section.focus({ preventScroll: true });
      return;
    }
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
}

// The trigger lives inside the nav, but the shortcut has to work from anywhere
// on the page. Only one of the two navs is mounted at a time (see
// layouts/default.vue), so there is never a second listener competing for it.
function onShortcut(event: KeyboardEvent) {
  // event.code names the physical key; event.key is what the active layout
  // produces, which on the Greek layout — the default locale's audience — is
  // "κ", so a key-based check would never fire for the people most likely to
  // use it.
  if ((event.metaKey || event.ctrlKey) && event.code === "KeyK") {
    event.preventDefault();
    open.value ? hide() : show();
  }
}

onMounted(() => window.addEventListener("keydown", onShortcut));

// useScrollLock releases its own hold on unmount.
onBeforeUnmount(() => window.removeEventListener("keydown", onShortcut));

// Navigating to a hash on the page you are already on does not unmount
// anything, so the panel has to be closed explicitly.
watch(() => route.fullPath, hide);
</script>

<template>
  <div class="site-search">
    <button ref="triggerRef" type="button" class="search-trigger"
            :aria-label="$t('searchTheSite')" aria-haspopup="dialog"
            :aria-expanded="open" @click="show">
      <Icon name="ph:magnifying-glass" :size="iconSize" class="trigger-icon"/>
    </button>

    <Teleport to="body">
      <Transition name="search-fade">
        <div v-if="open" class="search-overlay" @click.self="hide">
          <div ref="panelRef" class="search-panel" role="dialog" aria-modal="true"
               :aria-label="$t('searchTheSite')" @keydown.esc.stop="hide" @keydown.tab="onTab">

            <div class="search-field">
              <Icon name="ph:magnifying-glass" class="field-icon" aria-hidden="true"/>
              <input
                  ref="inputRef"
                  v-model="query"
                  type="search"
                  role="combobox"
                  aria-controls="site-search-results"
                  aria-autocomplete="list"
                  :aria-expanded="results.length > 0"
                  :aria-activedescendant="activeId"
                  :aria-label="$t('searchTheSite')"
                  :placeholder="$t('siteSearchPlaceholder')"
                  class="field-input"
                  @keydown.down.prevent="move(1)"
                  @keydown.up.prevent="move(-1)"
                  @keydown.enter.prevent="go(results[activeIndex])"
              />
              <button type="button" class="field-close" :aria-label="$t('closeSearch')" @click="hide">
                <Icon name="ph:x" size="1.4rem" aria-hidden="true"/>
              </button>
            </div>

            <p class="sr-only" aria-live="polite">
              {{ query ? $t('searchResultsFound', { count: results.length }) : '' }}
            </p>

            <!-- v-show, not v-if: the input's aria-controls points here, and a
                 reference to an element that does not exist is invalid ARIA.
                 A hidden listbox with aria-expanded=false is the combobox
                 pattern as written. -->
            <ul v-show="results.length" id="site-search-results" class="results"
                role="listbox" :aria-label="$t('searchResultsLabel')">
              <li v-for="(result, index) in results" :id="`site-search-result-${index}`"
                  :key="`${result.entry.path}${result.entry.hash ?? ''}${result.entry.titleKey}`"
                  role="option" :aria-selected="index === activeIndex"
                  class="result" :class="{ active: index === activeIndex }"
                  @mouseenter="activeIndex = index">
                <!-- The row is a link so middle-click and "open in new tab" behave. -->
                <nuxt-link :to="resolve(result.entry)" class="result-link" @click="hide">
                  <Icon :name="result.entry.icon" class="result-icon" aria-hidden="true"/>
                  <span class="result-text">
                    <span class="result-group">{{ $t(result.entry.groupKey) }}</span>
                    <span class="result-title">{{ result.title }}</span>
                    <span class="result-snippet">{{ result.snippet }}</span>
                  </span>
                  <Icon name="ph:arrow-right" class="result-arrow" aria-hidden="true"/>
                </nuxt-link>
              </li>
            </ul>

            <p v-if="query && !results.length" class="no-results">{{ $t('searchNoResults') }}</p>

            <!-- Shown while the field is empty and again when a query finds
                 nothing: a dead end is exactly when a visitor needs a next step. -->
            <div v-if="!results.length" class="examples">
              <p class="examples-label">{{ $t('searchExamplesLabel') }}</p>
              <div class="example-chips">
                <button v-for="example in examples" :key="example" type="button"
                        class="example-chip" @click="applyExample(example)">
                  <Icon name="ph:magnifying-glass" aria-hidden="true"/>
                  {{ example }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="sass" scoped>
// ── Tokens ────────────────────────────────────────────────────
// Shared values come from assets/sass/_variables.sass, which Vite injects into
// every style block. Only what has no global equivalent is declared here.
$text-muted: rgba(0, 0, 0, 0.45)
$text-muted-dark: rgba(255, 255, 255, 0.5)
$border: rgba(0, 0, 0, 0.08)
$border-dark: rgba(255, 255, 255, 0.12)
$surface: #f7f8f6
$surface-dark: rgba(255, 255, 255, 0.05)
$primary-focus: rgba($color-primary, 0.12)
$radius-sm: 10px

// ── Trigger ───────────────────────────────────────────────────
.search-trigger
  width: 45px
  height: 45px
  padding: 0
  border: none
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  border-radius: 50%
  color: $color-text-main
  background-color: rgba(0, 0, 0, 0.05)
  transition: background-color $transition-fast

  &:hover
    background-color: rgba(0, 0, 0, 0.1)

.dark-mode .search-trigger
  color: $color-text-light
  background-color: rgba(255, 255, 255, 0.1)

  &:hover
    background-color: rgba(255, 255, 255, 0.18)

// ── Overlay ───────────────────────────────────────────────────
// Above both navs: the floating desktop bar sits at z-index 100 and the mobile
// header at 160.
.search-overlay
  position: fixed
  inset: 0
  z-index: 200
  display: flex
  justify-content: center
  align-items: flex-start
  padding: 1.5rem
  padding-top: 12vh
  background-color: rgba(0, 0, 0, 0.45)
  backdrop-filter: $blur-standard

.search-panel
  width: 100%
  max-width: 620px
  max-height: 75vh
  display: flex
  flex-direction: column
  background-color: $color-light-bg
  border-radius: $border-radius-standard
  box-shadow: $shadow-hover
  overflow: hidden

.dark-mode .search-panel
  background-color: $color-surface-dark

// ── Field ─────────────────────────────────────────────────────
.search-field
  position: relative
  display: flex
  align-items: center
  gap: 0.5rem
  padding: 1rem 1rem 1rem 3rem
  border-bottom: 1px solid $border

  .field-icon
    position: absolute
    left: 1.15rem
    font-size: 1.2rem
    color: $text-muted
    pointer-events: none

.dark-mode .search-field
  border-bottom-color: $border-dark

  .field-icon
    color: $text-muted-dark

.field-input
  flex: 1
  min-width: 0
  padding: 0.4rem 0
  font-size: 1.05rem
  font-family: inherit
  font-weight: 500
  color: $color-text-main
  background: transparent
  border: none
  outline: none
  -webkit-appearance: none

  // type="search" keeps the semantics and the mobile "search" key, but the
  // browser also draws its own clear glyph, which -webkit-appearance on the
  // input does not remove. Without this the field shows two dismiss controls.
  &::-webkit-search-cancel-button
    -webkit-appearance: none
    display: none

  &::placeholder
    color: $text-muted

  &:focus-visible
    outline: none

.dark-mode .field-input
  color: $color-text-light

  &::placeholder
    color: $text-muted-dark

.field-close
  display: flex
  align-items: center
  justify-content: center
  padding: 0.25rem
  border: none
  border-radius: 50%
  background: none
  cursor: pointer
  color: $text-muted
  transition: color $transition-fast

  &:hover
    color: $color-text-main

.dark-mode .field-close
  color: $text-muted-dark

  &:hover
    color: $color-text-light

// ── Results ───────────────────────────────────────────────────
.results
  margin: 0
  padding: 0.5rem
  list-style: none
  overflow-y: auto

.result
  border-radius: $radius-sm

  &.active
    background-color: $surface

.dark-mode .result.active
  background-color: $surface-dark

.result-link
  display: flex
  align-items: center
  gap: 0.85rem
  padding: 0.7rem 0.85rem
  text-decoration: none
  color: inherit

.result-icon
  flex-shrink: 0
  font-size: 1.35rem
  color: $color-primary

.result-text
  flex: 1
  min-width: 0
  display: flex
  flex-direction: column
  gap: 0.15rem

.result-group
  font-size: 0.68rem
  font-weight: 800
  letter-spacing: 1.5px
  text-transform: uppercase
  color: $text-muted

.result-title
  font-size: 1rem
  font-weight: 700
  color: $color-text-main

.result-snippet
  font-size: 0.85rem
  color: $text-muted
  // Snippets are excerpts of long paragraphs; one line keeps rows scannable.
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.result-arrow
  flex-shrink: 0
  font-size: 1rem
  color: $text-muted
  opacity: 0
  transition: opacity $transition-fast

.result.active .result-arrow
  opacity: 1

.dark-mode
  .result-group,
  .result-snippet,
  .result-arrow
    color: $text-muted-dark

  .result-title
    color: $color-text-light

// ── Examples ──────────────────────────────────────────────────
.examples
  padding: 1.25rem 1.35rem 1.5rem

.examples-label
  margin: 0 0 0.75rem
  font-size: 0.68rem
  font-weight: 800
  letter-spacing: 1.5px
  text-transform: uppercase
  color: $text-muted

.example-chips
  display: flex
  flex-wrap: wrap
  gap: 0.45rem

.example-chip
  display: inline-flex
  align-items: center
  gap: 0.4rem
  padding: 0.45rem 0.85rem
  font-family: inherit
  font-size: 0.88rem
  font-weight: 600
  color: $color-text-main
  background: $surface
  border: 1px solid $border
  border-radius: 999px
  cursor: pointer
  transition: border-color $transition-fast, color $transition-fast

  &:hover
    color: $color-primary
    border-color: rgba($color-primary, 0.45)

.dark-mode
  .examples-label
    color: $text-muted-dark

  .example-chip
    color: $color-text-light
    background: $surface-dark
    border-color: $border-dark

    &:hover
      color: $color-primary
      border-color: rgba($color-primary, 0.5)

.no-results
  margin: 0
  padding: 1.75rem 1.5rem 0.5rem
  text-align: center
  font-size: 0.95rem
  color: $text-muted

// The example chips follow the no-results line; without this the two blocks'
// paddings stack into a gap that reads as two unrelated things.
.no-results + .examples
  padding-top: 0.75rem

.dark-mode .no-results
  color: $text-muted-dark

// ── Transition ────────────────────────────────────────────────
.search-fade-enter-active,
.search-fade-leave-active
  transition: opacity 200ms ease

  .search-panel
    transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1)

.search-fade-enter-from,
.search-fade-leave-to
  opacity: 0

  .search-panel
    transform: translateY(-12px)

@media (max-width: 640px)
  .search-overlay
    padding: 1rem
    padding-top: 5rem

  .result-snippet
    display: none
</style>
